import { Link, type LinkOptions, useRouter } from "@tanstack/react-router";
import { ChevronDown, XIcon } from "lucide-react";
import { type ReactNode, useMemo, useRef } from "react";
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  type Key,
  ListBox,
  type ListBoxItemProps,
  type ValidationResult,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { isNotNullOrUndefined } from "@/utils/types";

import { Button } from "./button";
import { Description, FieldError, FieldGroup, Input, Label } from "./field";
import { itemStyles } from "./form/fields/select";
import {
  DropdownItem,
  DropdownSection,
  type DropdownSectionProps,
} from "./list-box";
import { Popover } from "./popover";
import type { Option } from "./select";
import { composeTailwindRenderProps } from "./utils";

export interface ComboBoxProps<T extends Key> extends Omit<
  AriaComboBoxProps<Option<T>>,
  "children"
> {
  label?: ReactNode;
  description?: string | null;
  errorMessage?: string | ((validation: ValidationResult) => string);
  children: React.ReactNode | ((item: Option<T>) => React.ReactNode);
}

export function ComboBox<T extends Key>({
  label,
  description,
  errorMessage,
  children,
  items = [],
  placeholder,
  selectedKeys,
  multi,
  onRemove,
  ...props
}: ComboBoxProps<T> & {
  placeholder?: string;
  multi?: boolean;
  selectedKeys?: Iterable<T>;
  onRemove?: (key: T) => void;
}) {
  const itemsMap = new Map(Array.from(items).map((item) => [item.value, item]));

  const selectedKeysSet = new Set([
    props.selectedKey,
    ...(selectedKeys ? Array.from(selectedKeys) : []),
  ]);

  const selectedText = multi
    ? []
    : Array.from(items)
        .filter(({ value }) => selectedKeysSet.has(value))
        .map(({ display }) => display);

  const inputRef = useRef<HTMLInputElement>(null);

  const filteredItems = Array.from(items).filter(
    ({ value }) => !selectedKeysSet.has(value),
  );

  return (
    <AriaComboBox
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
    >
      {label && <Label isRequired={props.isRequired}>{label}</Label>}

      <FieldGroup>
        <Input
          placeholder={
            selectedText.length ? selectedText.join(", ") : placeholder
          }
          ref={inputRef}
          className={
            selectedText.length
              ? "placeholder:non-italic placeholder:text-black"
              : ""
          }
        />
        <Button
          variant="icon"
          color="muted"
          className="mr-1 w-6 rounded-xs rounded-l-none border-l border-gray-700 p-0 outline-offset-0"
        >
          <ChevronDown aria-hidden className="h-4 w-4" />
        </Button>
      </FieldGroup>

      {description && <Description>{description}</Description>}

      <FieldError>{errorMessage}</FieldError>

      {multi && selectedKeys && (
        <div className="flex flex-row items-center space-x-2 px-2">
          {Array.from(selectedKeys)
            .map((key) => itemsMap.get(key))
            .filter(isNotNullOrUndefined)
            .map(({ display, value, link }) =>
              link ? (
                <Link
                  key={value}
                  className="flex flex-row items-center space-x-2 rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                  {...link}
                >
                  <span>{display}</span>

                  <XIcon size={12} />
                </Link>
              ) : (
                <Button
                  key={value}
                  variant="icon"
                  className="flex flex-row items-center space-x-2 rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                  onPress={() => onRemove?.(value)}
                >
                  <span>{display}</span>

                  <XIcon size={12} />
                </Button>
              ),
            )}
        </div>
      )}

      <Popover className="w-(--trigger-width)">
        <ListBox
          items={filteredItems}
          className="max-h-[inherit] overflow-auto p-1 outline-0 [clip-path:inset(0_0_0_0_round_.75rem)]"
        >
          {filteredItems.map((item) => (
            <ComboBoxItem
              key={item.value}
              id={item.value}
              link={item.link}
              textValue={item.display}
            >
              {item.beforeDisplay}

              {item.display}
            </ComboBoxItem>
          ))}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}

export function ComboBoxItem<Value extends Key>({
  link,
  ...props
}: ListBoxItemProps<Option<Value>> & { link?: LinkOptions }) {
  const router = useRouter();

  const href = useMemo(() => {
    if (!link) {
      return undefined;
    }

    const location = router.buildLocation(link);

    return location.href;
  }, [router, link]);

  return <DropdownItem {...props} href={href} className={itemStyles} />;
}

export function ComboBoxSection<T extends object>(
  props: DropdownSectionProps<T>,
) {
  return <DropdownSection {...props} />;
}
